/**
 * PostMessage hook for Generate Assessment Plan page
 *
 * Manages bidirectional iframe communication with the parent window:
 * - Sends greeting when initialized
 * - Listens for dxList from parent
 * - Provides sendToParent helper for outbound messages
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseGenerateAssessmentPlanMessagesParams {
  isValidated: boolean;
  isValidating: boolean;
  scribeJobId: string | null;
  error: string | null;
}

export const useGenerateAssessmentPlanMessages = ({
  isValidated,
  isValidating,
  scribeJobId,
  error,
}: UseGenerateAssessmentPlanMessagesParams) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rawDxList, setRawDxList] = useState<any>(null);
  const hasGreeted = useRef(false);

  // Listen for messages from parent window
  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      console.log('[GenerateAssessmentPlan] Message received:', event.data);
      if (event.data.type === 'dxList') {
        setRawDxList(event.data.payload);
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, []);

  // Send greeting to parent when ready
  useEffect(() => {
    if (
      isValidated &&
      scribeJobId &&
      !isValidating &&
      !error &&
      !hasGreeted.current
    ) {
      hasGreeted.current = true;
      console.log(
        '[GenerateAssessmentPlan] Sending greeting to parent window...',
      );

      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'greeting',
            payload: {
              message: 'EyeVantage Generate Assessment Plan is ready',
              scribeJobId,
              sectionTag: 'assessmentAndPlan',
              timestamp: new Date().toISOString(),
            },
          },
          '*',
        );
        console.log('[GenerateAssessmentPlan] Greeting sent to parent window');
      }
    }
  }, [isValidated, scribeJobId, isValidating, error]);

  // Helper to send messages to parent
  const sendToParent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (type: string, payload: any) => {
      if (window.parent) {
        window.parent.postMessage(
          {
            type,
            payload,
            sectionTag: 'assessmentAndPlan',
          },
          '*',
        );
        console.log(
          `[GenerateAssessmentPlan] Sent '${type}' message to parent`,
        );
      }
    },
    [],
  );

  return {
    rawDxList,
    sendToParent,
  };
};
